import React, { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { roundToNearestValue } from '../utils';

import styles from './Counter.module.css';

interface CounterProps {
  defaultState?: number;
  min?: number;
  max?: number;
  txt?: string;
  allowInput?: boolean;
  incrementStep?: number;
  onCountChange?: (newCount: number) => void;
}

export function Counter({
  incrementStep = 1,
  defaultState = 0,
  min = undefined,
  max = undefined,
  txt,
  allowInput = false,
  onCountChange,
}: CounterProps) {
  const [count, setCount] = useState<number>(defaultState);
  const [inputVal, setInputVal] = useState<string>(String(defaultState));

  useEffect(() => {
    setCount(defaultState);
    setInputVal(String(defaultState));
  }, [defaultState]);

  useEffect(() => {
    setInputVal(count.toString());
    onCountChange?.(count);
  }, [count]);

  useEffect(() => {
    if (max < count) setCount(max);
  }, [max]);

  const sanitizeValue = useCallback(
    (value: number) => {
      if (min !== undefined) value = Math.max(min, value);
      if (max !== undefined) value = Math.min(max, value);
      if (Number.isFinite(value)) return value;
      if (min !== undefined) return min;
      return defaultState;
    },
    [min, max, defaultState],
  );

  const handleDecrement = useCallback(() => {
    setCount((prev) => sanitizeValue(prev - incrementStep));
  }, [incrementStep, sanitizeValue]);

  const handleIncrement = useCallback(() => {
    setCount((prev) => sanitizeValue(prev + incrementStep));
  }, [incrementStep, sanitizeValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    const numbVal = (val > 0 || min === 0) && sanitizeValue(val);
    setInputVal(Number.isFinite(numbVal) ? numbVal.toString() : '');
    // do not update count if value might be incorrect (incrementStep !== 1) or user provided an empty string - update is done on blur
    if (Number.isFinite(numbVal) && incrementStep === 1) setCount(numbVal);
  };

  const isDecrementDisabled = min !== undefined && count <= min;
  const isIncrementDisabled = max !== undefined && count >= max;

  const handleBlur = () => {
    let fallback: number;
    if (inputVal?.length === 0) fallback = min ?? defaultState;
    else if (incrementStep !== 1)
      fallback = roundToNearestValue(Number(inputVal), incrementStep);
    if (fallback !== undefined) {
      setInputVal(fallback.toString());
      setCount(fallback);
    }
  };

  return (
    <Stack className={styles.counter}>
      <IconButton
        color="primary"
        onClick={handleDecrement}
        aria-label="decrement"
        disabled={isDecrementDisabled}
        size="small"
      >
        <RemoveIcon />
      </IconButton>

      {allowInput ? (
        <TextField
          onBlur={handleBlur}
          value={inputVal === '0' ? inputVal.padStart(2, '0') : inputVal} // case: value=Nan
          onChange={handleInputChange}
          type="number"
          size="small"
          className={styles.inputField}
          onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" className={styles.endAdornment}>
                {txt}
                {count !== 1 && 's'}
              </InputAdornment>
            ),
            classes: {
              root: styles.inputRoot,
              input: styles.inputElement,
            },
            inputProps: {
              min,
              max,
            },
          }}
        />
      ) : (
        <Typography variant="body2">
          {count} {txt}
          {count !== 1 && 's'}
        </Typography>
      )}
      <IconButton
        color="primary"
        onClick={handleIncrement}
        aria-label="increment"
        disabled={isIncrementDisabled}
      >
        <AddIcon />
      </IconButton>
    </Stack>
  );
}
